/**
 * Responsibility: Shape tester access grants into field-assist form metadata and values.
 * Scope: Admin tester-access form metadata/value normalization only; persistence stays elsewhere.
 */
import type { FormMetaData } from 'field-assist'
import {
  COMMERCE_PLAN_IDS,
  type CommercePlanId,
  type CommerceTesterGrant,
  type CommerceTesterGrantKind,
  normalizeCommerceTesterGrant,
} from '../../commerce/commerce-contract'

const KIND_OPTIONS: Array<{ text: string; value: CommerceTesterGrantKind }> = [
  { text: 'Plan', value: 'plan' },
  { text: 'Marketplace World', value: 'marketplace_world' },
  { text: 'Preforged Offer', value: 'preforged_offer' },
]

const PLAN_OPTIONS: Array<{ text: string; value: CommercePlanId }> = COMMERCE_PLAN_IDS.map((id) => ({
  text: id[0].toUpperCase() + id.slice(1),
  value: id,
}))

export function buildAdminTesterInitialValues(grant?: Partial<CommerceTesterGrant>): Record<string, string> {
  return {
    email: String(grant?.email ?? ''),
    uid: String(grant?.uid ?? ''),
    displayName: String(grant?.displayName ?? ''),
    kind: String(grant?.kind ?? 'plan'),
    planId: String(grant?.planId ?? 'standard'),
    artifactId: String(grant?.artifactId ?? ''),
    offerId: String(grant?.offerId ?? ''),
    title: String(grant?.title ?? ''),
    state: String(grant?.state ?? 'active'),
  }
}

export function buildAdminTesterFormMeta(): FormMetaData {
  return {
    email: {
      label: 'Tester Email',
      componentType: 'input',
      attributes: {
        type: 'email',
        placeholder: 'tester@example.com',
        class: 'admin-role-input',
      },
    },
      uid: {
        label: 'Tester UID',
        componentType: 'input',
        attributes: {
          type: 'text',
          placeholder: 'Supabase user id (optional)',
          class: 'admin-role-input',
        },
      },
    displayName: {
      label: 'Display Name',
      componentType: 'input',
      attributes: {
        type: 'text',
        placeholder: 'optional label',
        class: 'admin-role-input',
      },
    },
    kind: {
      label: 'Grant Kind',
      componentType: 'select',
      options: KIND_OPTIONS,
      attributes: {
        class: 'admin-role-select',
      },
    },
    planId: {
      label: 'Plan',
      componentType: 'select',
      options: PLAN_OPTIONS,
      attributes: {
        class: 'admin-role-select',
      },
    },
    artifactId: {
      label: 'World Manifest ID',
      componentType: 'input',
      attributes: {
        type: 'text',
        placeholder: 'sample-choir-registry',
        class: 'admin-role-input',
      },
    },
    offerId: {
      label: 'Preforged Offer ID',
      componentType: 'input',
      attributes: {
        type: 'text',
        placeholder: 'choir-registry-reality',
        class: 'admin-role-input',
      },
    },
    title: {
      label: 'Grant Label',
      componentType: 'input',
      attributes: {
        type: 'text',
        placeholder: 'optional display label',
        class: 'admin-role-input',
      },
    },
    state: {
      label: 'Grant State',
      componentType: 'select',
      options: [
        { text: 'Active', value: 'active' },
        { text: 'Revoked', value: 'revoked' },
      ],
      attributes: {
        class: 'admin-role-select',
      },
    },
  }
}

export function buildAdminTesterSubmission(values: Record<string, unknown>): CommerceTesterGrant {
  const grant = normalizeCommerceTesterGrant({
    email: values.email,
    uid: values.uid,
    displayName: values.displayName,
    kind: values.kind,
    planId: values.planId,
    artifactId: values.artifactId,
    offerId: values.offerId,
    title: values.title,
    state: values.state,
  })
  if (!grant) throw new Error('Tester grants need an identity and a valid target.')
  return grant
}
